import { useState } from 'react'
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Grid,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Box,
  Typography,
  Chip,
  Autocomplete,
  Paper,
} from '@mui/material'
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  Remove as RemoveIcon,
} from '@mui/icons-material'
import { useCreateBill } from '../../hooks/api/useBills'
import { useCustomers } from '../../hooks/api/useCustomers'
import { useSKUs } from '../../hooks/api/useSKUs'
import { formatCurrency } from '../../utils/formatters'
import { useSelector } from 'react-redux'
import { RootState } from '../../store'
import { getCompanyUom } from '../../utils/companyUom'

interface BillItem {
  sku_id: string
  sku_name: string
  packing: number
  quantity_ctn: number
  loose_pcs: number
  quantity_pcs: number
  price: number
}

interface CreateBillModalProps {
  open: boolean
  onClose: () => void
}

const CreateBillModal = ({ open, onClose }: CreateBillModalProps) => {
  const { user } = useSelector((state: RootState) => state.auth)
  const uom = getCompanyUom(user?.company_uom)
  const cartonDiscountPricingEnabled = user?.company_features?.carton_discount_pricing === true
  const loosePcsEnabled = user?.company_features?.loose_pcs === true
  const [selectedCustomer, setSelectedCustomer] = useState<any>(null)
  const [billItems, setBillItems] = useState<BillItem[]>([])
  const [selectedSku, setSelectedSku] = useState('')
  const [quantity, setQuantity] = useState(1)
  const [loosePieces, setLoosePieces] = useState(0)
  const [remarks, setRemarks] = useState('')

  const [customerSearch, setCustomerSearch] = useState('')

  const createBillMutation = useCreateBill()
  const { data: customersData } = useCustomers({ page: 1, limit: 50, search: customerSearch || undefined })
  const { data: skusData } = useSKUs({ page: 1, limit: 100, active_only: true })

  const customers = customersData?.results || []
  const skus = skusData?.results || []

  const handleClose = () => {
    setSelectedCustomer(null)
    setBillItems([])
    setSelectedSku('')
    setQuantity(1)
    setLoosePieces(0)
    setRemarks('')
    setCustomerSearch('')
    onClose()
  }

  const handleAddItem = () => {
    if (!selectedSku || (quantity <= 0 && loosePieces <= 0)) return
    const sku = skus.find((s) => s._id === selectedSku)
    if (!sku || !Number.isInteger(loosePieces) || loosePieces < 0 || loosePieces >= sku.packing) return

    const existing = billItems.find((item) => item.sku_id === selectedSku)
    if (existing) {
      setBillItems((prev) =>
        prev.map((item) =>
          item.sku_id === selectedSku
            ? {
                ...item,
                quantity_ctn: item.quantity_ctn + quantity + Math.floor((item.loose_pcs + loosePieces) / item.packing),
                loose_pcs: (item.loose_pcs + loosePieces) % item.packing,
                quantity_pcs: (item.quantity_ctn + quantity) * item.packing + item.loose_pcs + loosePieces,
              }
            : item
        )
      )
    } else {
      setBillItems((prev) => [
        ...prev,
        {
          sku_id: selectedSku,
          sku_name: sku.name,
          packing: sku.packing,
          quantity_ctn: quantity,
          loose_pcs: loosePieces,
          quantity_pcs: quantity * sku.packing + loosePieces,
          price: sku.standard_rate,
        },
      ])
    }
    setSelectedSku('')
    setQuantity(1)
    setLoosePieces(0)
  }

  const handleRemove = (skuId: string) => {
    setBillItems((prev) => prev.filter((item) => item.sku_id !== skuId))
  }

  const handleIncrease = (skuId: string) => {
    setBillItems((prev) =>
      prev.map((item) =>
        item.sku_id === skuId
          ? {
              ...item,
              quantity_ctn: item.quantity_ctn + 1,
              quantity_pcs: (item.quantity_ctn + 1) * item.packing + item.loose_pcs,
            }
          : item
      )
    )
  }

  const handleDecrease = (skuId: string) => {
    setBillItems((prev) =>
      prev.map((item) =>
        item.sku_id === skuId
          ? {
              ...item,
              quantity_ctn: Math.max(0, item.quantity_ctn - 1),
              quantity_pcs: Math.max(0, item.quantity_ctn - 1) * item.packing + item.loose_pcs,
            }
          : item
      )
    )
  }

  const handleSubmit = () => {
    if (!selectedCustomer || billItems.length === 0) return
    createBillMutation.mutate(
      {
        customer_id: selectedCustomer._id,
        sku_items: billItems.map((item) => ({
          sku_id: item.sku_id,
          quantity_ctn: item.quantity_ctn,
          loose_pcs: item.loose_pcs,
        })),
        remarks,
      },
      { onSuccess: handleClose }
    )
  }

  const totalCtn = billItems.reduce((s, i) => s + i.quantity_ctn + i.loose_pcs / i.packing, 0)
  const totalPcs = billItems.reduce((s, i) => s + i.quantity_pcs, 0)
  const estimatedTotal = billItems.reduce((s, i) => s + i.price * (i.quantity_ctn + i.loose_pcs / i.packing), 0)

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <DialogTitle>Create New Bill</DialogTitle>

      <DialogContent dividers>
        <Grid container spacing={2}>
          {/* Customer selector */}
          <Grid item xs={12}>
            <Autocomplete
              options={customers}
              value={selectedCustomer}
              onChange={(_, value) => setSelectedCustomer(value)}
              inputValue={customerSearch}
              onInputChange={(_, value) => setCustomerSearch(value)}
              filterOptions={(x) => x}
              getOptionLabel={(c) => c.name}
              renderOption={(props, c) => (
                <Box component="li" {...props}>
                  <Box>
                    <Box display="flex" alignItems="center" gap={0.5}>
                      <Typography variant="body2">{c.name}</Typography>
                      {c.is_sales_tax_customer && (
                        <Chip
                          label="STI"
                          size="small"
                          color="warning"
                          variant="outlined"
                          sx={{ height: 16, fontSize: '0.65rem' }}
                        />
                      )}
                    </Box>
                    <Typography variant="caption" color="text.secondary">
                      {c.area_id?.area_name || 'No Area'} •{' '}
                      {typeof c.booker_id === 'object' ? c.booker_id?.name : 'No Booker'}
                    </Typography>
                  </Box>
                </Box>
              )}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Customer"
                  required
                  placeholder="Search by name..."
                />
              )}
              isOptionEqualToValue={(a, b) => a._id === b._id}
            />
          </Grid>

          {/* Customer info strip */}
          {selectedCustomer && (
            <Grid item xs={12}>
              <Box
                sx={{
                  p: 1.5,
                  bgcolor: 'grey.50',
                  borderRadius: 1,
                  display: 'flex',
                  flexWrap: 'wrap',
                  gap: 2,
                  alignItems: 'center',
                }}
              >
                <Typography variant="body2">
                  <strong>Area:</strong> {selectedCustomer.area_id?.area_name || 'N/A'}
                </Typography>
                <Typography variant="body2">
                  <strong>Booker:</strong>{' '}
                  {typeof selectedCustomer.booker_id === 'object'
                    ? selectedCustomer.booker_id?.name
                    : 'N/A'}
                </Typography>
                <Typography variant="body2">
                  <strong>{cartonDiscountPricingEnabled ? 'Customer Type' : 'Discount'}:</strong>{' '}
                  {cartonDiscountPricingEnabled
                    ? selectedCustomer.customer_type === 'wholesale' ? 'Wholesale' : 'Retail'
                    : `${selectedCustomer.discount || 0}%`}
                </Typography>
                {selectedCustomer.is_sales_tax_customer && (
                  <Chip label="Sales Tax Invoice" size="small" color="warning" />
                )}
              </Box>
            </Grid>
          )}

          {/* SKU picker */}
          <Grid item xs={12} sm={5}>
            <FormControl fullWidth>
              <InputLabel>SKU</InputLabel>
              <Select
                value={selectedSku}
                onChange={(e) => setSelectedSku(e.target.value)}
                label="SKU"
              >
                {skus.map((sku) => (
                  <MenuItem key={sku._id} value={sku._id}>
                    {sku.name} — Pack: {sku.packing} — Rs {sku.standard_rate}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={12} sm={2}>
            <TextField
              fullWidth
              label={`Qty (${uom.plural})`}
              type="number"
              value={quantity}
              onChange={(e) => setQuantity(Math.max(0, Number(e.target.value)))}
              inputProps={{ min: 0, step: uom.allows_decimal ? 0.001 : 1 }}
            />
          </Grid>

          {loosePcsEnabled && <Grid item xs={12} sm={2}>
            <TextField
              fullWidth
              label="Loose PCS"
              type="number"
              value={loosePieces}
              onChange={(e) => setLoosePieces(Math.max(0, Math.floor(Number(e.target.value))))}
              inputProps={{ min: 0, max: (skus.find((sku) => sku._id === selectedSku)?.packing || 1) - 1, step: 1 }}
              error={Boolean(selectedSku && loosePieces >= (skus.find((sku) => sku._id === selectedSku)?.packing || 1))}
            />
          </Grid>}

          <Grid item xs={12} sm={3} sx={{ display: 'flex' }}>
            <Button
              fullWidth
              variant="outlined"
              startIcon={<AddIcon />}
              onClick={handleAddItem}
              disabled={!selectedSku || (quantity <= 0 && loosePieces <= 0) || loosePieces >= (skus.find((sku) => sku._id === selectedSku)?.packing || 1)}
            >
              Add Item
            </Button>
          </Grid>

          {/* Items table */}
          {billItems.length > 0 && (
            <Grid item xs={12}>
              <TableContainer component={Paper} variant="outlined">
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>SKU</TableCell>
                      <TableCell align="right">Pack</TableCell>
                      <TableCell align="center">{uom.plural}</TableCell>
                      {loosePcsEnabled && <TableCell align="right">Loose PCS</TableCell>}
                      <TableCell align="right">Pieces</TableCell>
                      <TableCell align="right">Rate</TableCell>
                      <TableCell align="right">Amount</TableCell>
                      <TableCell width={40} />
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {billItems.map((item) => (
                      <TableRow key={item.sku_id}>
                        <TableCell>{item.sku_name}</TableCell>
                        <TableCell align="right">{item.packing}</TableCell>
                        <TableCell align="center">
                          <Box display="flex" alignItems="center" justifyContent="center" gap={0.5}>
                            <IconButton
                              size="small"
                              onClick={() => handleDecrease(item.sku_id)}
                              disabled={item.quantity_ctn <= 0 || (item.quantity_ctn <= 1 && item.loose_pcs === 0)}
                            >
                              <RemoveIcon fontSize="small" />
                            </IconButton>
                            <Typography variant="body2" minWidth="24px" textAlign="center">
                              {item.quantity_ctn}
                            </Typography>
                            <IconButton size="small" onClick={() => handleIncrease(item.sku_id)}>
                              <AddIcon fontSize="small" />
                            </IconButton>
                          </Box>
                        </TableCell>
                        {loosePcsEnabled && <TableCell align="right">
                          <TextField
                            type="number"
                            size="small"
                            value={item.loose_pcs}
                            inputProps={{ min: 0, max: item.packing - 1, step: 1, style: { width: 55 } }}
                            onChange={(event) => {
                              const loose = Number(event.target.value)
                              if (!Number.isInteger(loose) || loose < 0 || loose >= item.packing || (loose === 0 && item.quantity_ctn === 0)) return
                              setBillItems((prev) => prev.map((row) => row.sku_id === item.sku_id
                                ? { ...row, loose_pcs: loose, quantity_pcs: row.quantity_ctn * row.packing + loose }
                                : row))
                            }}
                          />
                        </TableCell>}
                        <TableCell align="right">{item.quantity_pcs}</TableCell>
                        <TableCell align="right">Rs {formatCurrency(item.price)}</TableCell>
                        <TableCell align="right">
                          Rs {formatCurrency(item.price * (item.quantity_ctn + item.loose_pcs / item.packing))}
                        </TableCell>
                        <TableCell>
                          <IconButton
                            size="small"
                            color="error"
                            onClick={() => handleRemove(item.sku_id)}
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    ))}

                    {/* Totals row */}
                    <TableRow sx={{ bgcolor: 'grey.50' }}>
                      <TableCell colSpan={2}>
                        <Typography variant="body2" fontWeight={600}>
                          Total
                        </Typography>
                      </TableCell>
                      <TableCell align="center">
                        <Typography variant="body2" fontWeight={600}>
                          {Number(totalCtn.toFixed(3))} {uom.code}
                        </Typography>
                      </TableCell>
                      <TableCell />
                      <TableCell align="right">
                        <Typography variant="body2" fontWeight={600}>
                          {totalPcs} pcs
                        </Typography>
                      </TableCell>
                      <TableCell />
                      <TableCell align="right">
                        <Typography variant="body2" fontWeight={600}>
                          Rs {formatCurrency(estimatedTotal)}
                        </Typography>
                      </TableCell>
                      <TableCell />
                    </TableRow>
                  </TableBody>
                </Table>
              </TableContainer>
              <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                * Final amount including taxes is calculated server-side
              </Typography>
            </Grid>
          )}

          {/* Remarks */}
          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Remarks"
              value={remarks}
              onChange={(e) => setRemarks(e.target.value)}
              multiline
              rows={2}
              placeholder="Optional"
            />
          </Grid>
        </Grid>
      </DialogContent>

      <DialogActions>
        <Button onClick={handleClose} disabled={createBillMutation.isPending}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={!selectedCustomer || billItems.length === 0 || createBillMutation.isPending}
        >
          {createBillMutation.isPending ? 'Creating...' : 'Create Bill'}
        </Button>
      </DialogActions>
    </Dialog>
  )
}

export default CreateBillModal
